import {
  assignPrototypeAPIs,
  assignTableAPIs,
  makeStateUpdater,
  type OnChangeFn,
  type Row,
  type RowData,
  type Table,
  type TableFeature,
  type TableFeatures,
  type Updater,
} from "@tanstack/react-table";

type EditFeatureState = Record<string, boolean>;

export interface EditFeatureTableState {
  editing: EditFeatureState;
}

export interface EditFeatureOptions {
  onEditChange?: OnChangeFn<EditFeatureState>;
}

export interface EditFeatureRow {
  getIsEditing: () => boolean;
  toggleEditing: (editing: boolean) => void;
}

export interface EditFeatureInstance {
  setEditing: (updater: Updater<EditFeatureState>) => void;
}

declare module "@tanstack/table-core" {
  interface Plugins {
    editFeature: TableFeature;
  }
  interface TableState_FeatureMap {
    editFeature: EditFeatureTableState;
  }
  interface TableOptions_FeatureMap<
    TFeatures extends TableFeatures,
    TData extends RowData,
  > {
    editFeature: EditFeatureOptions;
  }
  interface Table_FeatureMap<
    TFeatures extends TableFeatures,
    TData extends RowData,
  > {
    editFeature: EditFeatureInstance;
  }
  interface Row_FeatureMap<
    TFeatures extends TableFeatures,
    TData extends RowData,
  > {
    editFeature: EditFeatureRow;
  }
}

interface EditFeatures {
  editFeature: TableFeature;
}
type EditTable = Table<EditFeatures, RowData>;
type EditRow = Row<EditFeatures, RowData>;

export const editFeature: TableFeature = {
  getInitialState: (state) => ({
    editing: {},
    ...state,
  }),

  getDefaultTableOptions: (table) => ({
    onEditChange: makeStateUpdater("editing", table),
  }),

  constructTableAPIs: (table) => {
    const t = table as unknown as EditTable;

    assignTableAPIs("editFeature", table, {
      table_setEditing: {
        fn: (updater: Updater<EditFeatureState>) =>
          t.options.onEditChange?.(updater),
      },
    });
  },

  assignRowPrototype: (prototype, table) => {
    assignPrototypeAPIs("editFeature", prototype, table, {
      row_getIsEditing: {
        fn: (row) => {
          const r = row as unknown as EditRow;

          return r.table.atoms.editing.get()[r.id] ?? false;
        },
      },
      row_toggleEditing: {
        fn: (row, editing: boolean) => {
          const r = row as unknown as EditRow;
          const isEditing = r.getIsEditing();

          r.table.setEditing((old) => {
            if (isEditing === editing) {
              return old;
            }

            const state = { ...old };

            if (editing) {
              state[r.id] = true;
            } else {
              delete state[r.id];
            }

            return state;
          });
        },
      },
    });
  },
};
